import { enableProdMode, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';

import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';
import { provideRouter } from '@angular/router';
import { routes } from './app/app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { loadingInterceptor } from './app/core/interceptors/loading.interceptor.fn';
import { httpTokenInterceptor } from './app/core/interceptors/http-token.interceptor.fn';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import { LOCALE_ID } from '@angular/core';
import { provideTransloco } from '@jsverse/transloco';
import { isDevMode } from '@angular/core';
import { importProvidersFrom } from '@angular/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { TranslocoHttpLoader } from './app/transloco-root.module';
import { providePrimeNG } from 'primeng/config';
import { StaffMetricsPreset } from './app/theme/staffmetrics-preset';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { ConfigurationService } from './app/core/services/configuration.service';
import { SubscriptionService } from './app/core/services/subscription.service';
import { AuthService } from './app/core/services/auth.service';
import { provideServiceWorker } from '@angular/service-worker';
import { ChunkErrorHandler } from './app/core/error/chunk-error.handler';

if (environment.production) {
  enableProdMode();
  //show this warning only on prod mode
  if (window) {
    selfXSSWarning();
  }
}

// Runtime version check
const storedVersion = localStorage.getItem('appVersion');
const currentVersion = (window as any)?.env?.appVersion || '1.0.0';

document.querySelector('meta[name="app-version"]')?.setAttribute('content', currentVersion);

if (storedVersion && storedVersion !== currentVersion) {
  localStorage.clear();
  sessionStorage.clear();
  window.location.reload();
} else {
  localStorage.setItem('appVersion', currentVersion);
}

registerLocaleData(localeFr, 'fr');

// Configuration initializer factory
// Both need the token: before signing in the server refuses them, so they are
// read here only for a session already open, and by the sign-in page otherwise
// (AuthService.loadSessionContext).
function initializeAppConfig(configService: ConfigurationService, auth: AuthService) {
  return (): Promise<void> => (auth.isAuthenticated() ? configService.initializeConfigurations() : Promise.resolve());
}

// Subscription initializer factory — guards and menus read the plan
// synchronously, so it has to be in place before the first route resolves.
function initializeSubscription(subscriptionService: SubscriptionService, auth: AuthService) {
  return (): Promise<void> =>
    auth.isAuthenticated() ? subscriptionService.initializeSubscription() : Promise.resolve();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),

    // Re-registered after the move to standalone components dropped the AppModule that used
    // to do it. angular.json has been building ngsw-worker.js the whole time, so every
    // deployment shipped a worker nobody started -- while browsers that visited before that
    // migration kept running the one they already had, with nothing left to update it.
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),

    // A tab open across a deploy asks for lazy chunks that no longer exist. AppUpdateService
    // is the graceful path and handles almost every case; this is the net under it, for a
    // browser with no worker registered yet.
    { provide: ErrorHandler, useClass: ChunkErrorHandler },

    provideHttpClient(withInterceptors([loadingInterceptor, httpTokenInterceptor])),
    provideAnimations(),
    DialogService,
    MessageService,
    importProvidersFrom(AngularSvgIconModule.forRoot()),
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr', 'ar'],
        defaultLang: 'fr',
        fallbackLang: 'fr',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    providePrimeNG({
      ripple: true,
      theme: {
        preset: StaffMetricsPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark',
          cssLayer: true,
        },
      },
    }),
    // Initialize configurations on app startup
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppConfig,
      deps: [ConfigurationService, AuthService],
      multi: true,
    },

    // Load the deployment's subscription plan on app startup
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSubscription,
      deps: [SubscriptionService, AuthService],
      multi: true,
    },
  ],
}).catch((err) => console.error(err));

function selfXSSWarning() {
  setTimeout(() => {
    // console.log('%c** STOP **', 'font-weight:bold; font: 2.5em Arial; color: white; background-color: #e11d48; padding-left: 15px; padding-right: 15px; border-radius: 25px; padding-top: 5px; padding-bottom: 5px;');
    // console.log(
    //   `\n%cThis is a browser feature intended for developers. Using this console may allow attackers to impersonate you and steal your information sing an attack called Self-XSS. Do not enter or paste code that you do not understand.`, 'font-weight:bold; font: 2em Arial; color: #e11d2;'
    // );
  });
}
